import { AccountAccess } from '../../components/account-access/account-access';
import { Component, HostListener, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, OnDestroy, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { NavigationStart, Router, RouterLink } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MegaMenuConfig, MegaMenuLink } from '../../models/mega-menu.model';
import { MegaMenuComponent } from '../../components/mega-menu/mega-menu.component';
import { MobileMenuComponent } from '../../components/mobile-menu/mobile-menu.component';
import { SessionService } from '../../services/session.service';
import { TeamSectionsService } from '../../services/team-sections.service';
import { CartService } from '../../../features/store/services/cart.service';
import { CartCountdown } from '../../../features/store/components/cart-countdown/cart-countdown';
import { CatalogService } from '../../../features/store/services/catalog.service';
import { filterProductsByCategory } from '../../../features/store/mappers/catalog.mapper';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink, MegaMenuComponent, MobileMenuComponent, AccountAccess, CartCountdown],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Header implements OnDestroy {
  private readonly sessionService = inject(SessionService);
  readonly usuario = this.sessionService.usuario;
  readonly cartCount = inject(CartService).unitCount;
  private readonly teamSections = inject(TeamSectionsService).sections;
  private readonly destroyRef = inject(DestroyRef);
  // Algunos tests livianos del shell no instalan HttpClient. En la aplicación real sí está
  // disponible y entonces se carga el catálogo dinámico del megamenú.
  private readonly catalog = inject(HttpClient, { optional: true })
    ? inject(CatalogService)
    : null;
  activeMenu: 'store' | 'plantel' | null = null;
  isClosing = false;
  isMobileMenuOpen = false;
  mobileSubmenu: 'store' | 'plantel' | null = null;
  private closeTimeout: ReturnType<typeof setTimeout> | null = null;
  private navigationSubscription: Subscription;
  private readonly CLOSE_DURATION = 240;

  readonly storeMenuConfig = signal<MegaMenuConfig>({
    mainTitle: 'TIENDA',
    mainUrl: '/store',
    columns: [],
  });

  // Una columna por sección que devuelve el backend; si no hay secciones, el menú queda sin columnas.
  readonly plantelMenuConfig = computed<MegaMenuConfig>(() => ({
    mainTitle: 'PLANTELES',
    mainUrl: '/squad',
    columns: this.teamSections().map((section) => {
      const name = section.name.toUpperCase();
      const queryParams = { equipo: section.slug };
      const links: MegaMenuLink[] = [{ label: `PLANTEL ${name}`, url: '/squad', queryParams }];
      if (section.sport.code === 'FUTSAL') {
        links.push({ label: 'CUERPO TÉCNICO', url: '/squad', queryParams, fragment: 'cuerpo-tecnico' });
      }
      return { title: name, links };
    }),
  }));

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {
    this.navigationSubscription = router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => this.closeActiveMenu());
    this.loadStoreMenu();
  }

  private loadStoreMenu(): void {
    if (!this.catalog) return;
    forkJoin({
      categories: this.catalog.listCategories(),
      products: this.catalog.listProducts(),
      subCategories: this.catalog.listSubCategories(),
      branches: this.catalog.listBranches(),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ categories, products, subCategories, branches }) => {
        this.storeMenuConfig.set({
          mainTitle: 'TIENDA',
          mainUrl: '/store',
          columns: categories.map(category => {
            const categoryProducts = filterProductsByCategory(
              products,
              Number(category.id),
              subCategories,
              branches,
            );
            const links: MegaMenuLink[] = categoryProducts.slice(0, 3).map(product => ({
              label: product.title,
              url: `/store/product/${product.id}`,
            }));
            if (categoryProducts.length > 3) {
              links.push({ label: '…', url: '', isEllipsis: true });
            }
            return {
              title: category.name,
              url: '/store/categories',
              queryParams: { categoryId: category.id },
              links,
            };
          }),
        });
        this.cdr.markForCheck();
      },
      // Si el catálogo no está disponible, "TIENDA" conserva su navegación principal.
      error: () => undefined,
    });
  }

  ngOnDestroy(): void {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
    }
    this.navigationSubscription.unsubscribe();
  }

  toggleMenu(menu: 'store' | 'plantel', event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.activeMenu === menu && !this.isClosing) {
      this.closeActiveMenu();
    } else {
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }
      this.isClosing = false;
      this.activeMenu = menu;
      this.cdr.markForCheck();
    }
  }

  closeActiveMenu(): void {
    if (this.activeMenu && !this.isClosing) {
      this.isClosing = true;
      this.cdr.markForCheck();
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
      }
      this.closeTimeout = setTimeout(() => {
        this.activeMenu = null;
        this.isClosing = false;
        this.closeTimeout = null;
        this.cdr.markForCheck();
      }, this.CLOSE_DURATION);
    }
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    if (!this.isMobileMenuOpen) {
      this.mobileSubmenu = null;
    }
    this.cdr.markForCheck();
  }

  toggleMobileSubmenu(submenu: 'store' | 'plantel'): void {
    this.mobileSubmenu = this.mobileSubmenu === submenu ? null : submenu;
    this.cdr.markForCheck();
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
    this.mobileSubmenu = null;
    this.cdr.markForCheck();
  }

  signOut(): void {
    this.sessionService.cerrarSesion();
    this.closeMobileMenu();
    void this.router.navigate(['/']);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isClickInsideMegaMenu(event.target as Node)) {
      this.closeActiveMenu();
    }
  }

  private isClickInsideMegaMenu(target: Node): boolean {
    const megaMenus: NodeListOf<HTMLElement> =
      this.elementRef.nativeElement.querySelectorAll('app-mega-menu');
    for (let i = 0; i < megaMenus.length; i++) {
      if (megaMenus[i].contains(target)) {
        return true;
      }
    }
    return false;
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.activeMenu) {
      this.closeActiveMenu();
    }
  }
}
